// ============================================================
// ✅ PosteModal.jsx - Création d'un poste
// Support multi-tenant : Global (NULL) ou Société spécifique
// Admin : société automatique, SuperAdmin : choix Global/Société
// ============================================================

import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import { useUser } from '../../contexts/UserContext';
import { X, AlertCircle, Globe } from 'lucide-react';

export default function PosteModal({ isOpen, onClose, onSuccess }) {
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [services, setServices] = useState([]);
  const [societes, setSocietes] = useState([]);
  
  const [formData, setFormData] = useState({
    Nom_Postes: '', ID_Services: '', ID_Societe: '', EstGlobal: false
  });

  const { user } = useUser();
  const isSuperAdmin = user?.EstSuperAdmin === 1 || user?.EstSuperAdmin === true;

  useEffect(() => {
    if (isOpen) {
      resetForm();
      fetchServices();
      if (isSuperAdmin) fetchSocietes();
    }
  }, [isOpen]);

  const fetchServices = async () => {
    try {
      const res = await api.get('/services', { params: { ID_Societe: isSuperAdmin ? null : user?.ID_Societe } });
      setServices(res.data.services || []);
    } catch (err) { console.error('Erreur chargement services:', err); }
  };

  const fetchSocietes = async () => {
    try { const res = await api.get('/societes'); setSocietes(res.data.societes || []); }
    catch (err) { console.error('Erreur chargement sociétés:', err); }
  };

  const resetForm = () => {
    setFormData({ Nom_Postes: '', ID_Services: '', ID_Societe: user?.ID_Societe || '', EstGlobal: false });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.Nom_Postes.trim() || !formData.ID_Services) { setError('Tous les champs sont obligatoires'); return; }
    setLoading(true); setError('');
    try {
      const payload = {
        Nom_Postes: formData.Nom_Postes,
        ID_Services: formData.ID_Services,
        ID_Societe: formData.EstGlobal ? null : (isSuperAdmin ? (formData.ID_Societe || null) : user?.ID_Societe),
        EstSuperAdmin: isSuperAdmin ? 1 : 0
      };
      await api.post('/postes', payload);
      onSuccess(); onClose();
    } catch (err) { setError(err.response?.data?.message || 'Erreur'); }
    finally { setLoading(false); }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl p-6 w-full max-w-md">
        <div className="flex justify-between items-center mb-4"><h3 className="text-lg font-semibold text-emerald-600">💼 Nouveau poste</h3><button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={20} /></button></div>
        {error && <div className="mb-4 p-3 bg-red-50 text-red-600 text-sm rounded-lg flex items-center gap-2"><AlertCircle size={16} /> {error}</div>}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div><label className="block text-sm font-medium text-gray-700 mb-1">Nom du poste *</label><input type="text" className="w-full p-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500" placeholder="Ex: Développeur" value={formData.Nom_Postes} onChange={(e) => setFormData({...formData, Nom_Postes: e.target.value})} required /></div>
          <div><label className="block text-sm font-medium text-gray-700 mb-1">Service *</label><select className="w-full p-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500" value={formData.ID_Services} onChange={(e) => setFormData({...formData, ID_Services: e.target.value})} required><option value="">Sélectionner un service</option>{services.map(s => <option key={s.ID_Services} value={s.ID_Services}>{s.Nom_Services}</option>)}</select></div>
          
          {isSuperAdmin && <label className="flex items-center gap-3 p-3 bg-purple-50 rounded-lg cursor-pointer border border-purple-200"><input type="checkbox" checked={formData.EstGlobal} onChange={(e) => setFormData({...formData, EstGlobal: e.target.checked, ID_Societe: e.target.checked ? '' : (user?.ID_Societe || '')})} className="w-4 h-4 text-purple-600 rounded" /><div><span className="text-sm font-medium text-purple-700 flex items-center gap-1"><Globe size={14} /> Poste Global</span><p className="text-xs text-purple-500">Visible par toutes les sociétés</p></div></label>}
          
          {!formData.EstGlobal && isSuperAdmin && <div><label className="block text-sm font-medium text-gray-700 mb-1">Société *</label><select className="w-full p-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500" value={formData.ID_Societe || ''} onChange={(e) => setFormData({...formData, ID_Societe: e.target.value})} required><option value="">Sélectionner</option>{societes.map(s => <option key={s.ID_Societe} value={s.ID_Societe}>{s.RaisonSociale} ({s.Sigle})</option>)}</select></div>}
          
          {!formData.EstGlobal && !isSuperAdmin && <div className="p-2.5 bg-gray-50 rounded-lg text-sm text-gray-600">Société : <strong>{user?.SocieteNom || `ID: ${user?.ID_Societe}`}</strong></div>}
          
          <div className="flex gap-3 pt-2"><button type="button" onClick={onClose} className="flex-1 px-4 py-2.5 bg-gray-100 text-gray-700 rounded-lg text-sm">Annuler</button><button type="submit" disabled={loading} className="flex-1 px-4 py-2.5 bg-emerald-500 text-white rounded-lg text-sm">{loading ? 'Création...' : 'Créer'}</button></div>
        </form>
      </div>
    </div>
  );
}